// src/components/navigation/constants/navigationItems.ts
import {
    Home,
    Calendar,
    Music,
    Building,
    User,
    FileText,
    ScanLine,
    BookOpen,
    BarChart2,
    Settings,
    Wrench,
    Users,
    Layout,
    PenTool,
    Mail,
    CalendarDays
  } from 'lucide-react';
  import type { NavigationSection } from '../../../types/navigation';


  
  export const getNavigationItems = (role: string, userName?: string): NavigationSection => {
    const musicianPath = userName
      ? `/musician/${encodeURIComponent(userName)}`
      : '/artist/profile';
    switch (role) {
      case 'superadmin':
      case 'admin':
      case 'gm':
        return {
          mainItems: [
            {
              label: 'Bands',
              icon: Music,
              children: [
                { label: 'Band List', path: '/admin/bands', icon: Music },
                { label: 'Pending Bands', path: '/admin/pending-bands', icon: Music },
                { label: 'Artist Analytics', path: '/admin/analytics', icon: BarChart2 },
                { label: 'Artist Invitations', path: '/admin/artist-invitations', icon: Mail },
                { label: 'Restore', path: '/admin/deleted-drafts', icon: Wrench },
                { label: 'Stage Designer', path: '/stage-designer', icon: Layout },
                { label: 'Promotional Strands', path: '/admin/strands/live', icon: FileText },
              ],
            },
            {
              label: 'Rota',
              icon: Calendar,
              children: [
                { label: 'Calendar', path: '/calendar', icon: Calendar },
                { label: 'Rota', path: '/admin/rota', icon: Calendar },
              ],
            },
          ],
          dropdowns: {
            'Gigs': [
              { label: 'Programming', path: '/admin/programming', icon: Music },
              { label: 'Performances', path: '/admin/performances' },
              {
                label: 'Strands',
                children: [
                  { label: 'Strand List', path: '/admin/strands' },
                  { label: 'Promotional Strands', path: '/admin/strands/live' },
                  { label: 'Public Strands', path: '/publicstrands' },
                ]
              },
            ],
            Finance: [
              { label: 'Add Expense', path: '/admin/expenses/new' },
              { label: 'Create Invoice', path: '/admin/invoices/new' },
              {
                label: 'Budgets',
                children: [
                  { label: 'Dashboard', path: '/admin/budget-dashboard' },
                  { label: 'Budget Overview', path: '/admin/budget-overview' },
                  { label: 'Budgets List', path: '/admin/master-budget-list' },
                  { label: 'AAR Budgets', path: '/admin/aar' },
                  { label: 'Budget Manager', path: '/admin/budget-manager' },
                  { label: 'Marketing Slice', path: '/admin/marketing-budget' },
                  { label: 'Ticketing Slice', path: '/admin/ticketing-budget' },
                  { label: 'Fees Slice', path: '/admin/fees-budget' },
                  { label: 'Production Slice', path: '/admin/production-budget' }
                ]
              },
            ],
            'Venues & Production': [
              { label: 'Venue List', path: '/admin/venues', icon: Building },
              { label: 'Add Venue', path: '/admin/venues/new' },
              { label: 'Cooler', path: '/admin/cooler' },
              { label: 'Stage Designer', path: '/stage-designer' },
            ],
            'Marketing & Sales': [
              { label: 'Sales Dashboard', path: '/admin/sales', icon: BarChart2 },
              { label: 'Analysis', path: '/admin/performances/review' },
              {
                label: 'Ticketing',
                children: [
                  { label: 'Ticket Management', path: '/tix' },
                  { label: 'Ticket List', path: '/tix-list' }
                ]
              },
              { label: 'Portfolios', path: '/admin/portfolios' }
            ],
            Applications: [
              { label: 'Received', path: '/admin/applications', icon: Mail },
              { label: 'Forms', path: '/Formbuilder', icon: BookOpen },
              { label: 'Guidelines', path: '/guidelines' }
            ],
            'System Admin': [
              { label: 'User Dashboard', path: '/admin/personnel/dashboard/', icon: Settings },
              { label: 'User Management', path: '/admin/users', icon: Users },
              { label: 'Personnel', path: '/admin/personnel', icon: Users },
              ...(role === 'superadmin' || role === 'gm'
                ? [{ label: 'Leave Management', path: '/admin/leave-management', icon: CalendarDays }]
                : []),
            ],
            Tools: [
              { label: 'Pomodoro', path: '/musicadmin', icon: Wrench },
              { label: 'Quick Ideas', path: '/admin/ideas', icon: PenTool },
              { label: 'QuickTasks', path: '/tasks', icon: ScanLine },
            ]
          }
        };
      case 'artist':
        return {
          mainItems: [
            { label: 'My Dashboard', path: '/artist/dashboard', icon: Home },
            { label: 'My Profile', path: musicianPath, icon: User },
            { label: 'Submit A New Band', path: '/submit', icon: Music },
          ],
          dropdowns: {
            More: [
              { label: 'My Performances', path: '/artist/performances' },
              { label: 'Calendar', path: '/artist/calendar' },
              { label: 'My Pomodoro', path: '/musicadmin' },
              { label: 'Stage Plot Creator', path: '/stage-designer' },
            ],
          },
        };
        case 'team':
          return {
            mainItems: [
              { label: 'My Dashboard', path: '/team/dashboard', icon: Home },
              { label: 'My Leave', path: '/admin/my-leave-and-til', icon: CalendarDays },
            ],
              
            dropdowns: {
              'Events & Programming': [
                { label: 'Programming', path: '/admin/programming', icon: Music },
                { label: 'Performances', path: '/admin/performances' },
                { 
                  label: 'Events',
                  children: [
                    { label: 'Events List', path: '/admin/events' },
                  ]
                },
                { 
                  label: 'Strands',
                  children: [
                    { label: 'Strand List', path: '/admin/strands' },
                    { label: 'Published Strands', path: '/publicstrands' },
                  ]
                },
              ],
              'Venues & Production': [
                { label: 'Venue List', path: '/admin/venues', icon: Building },
                { label: 'Add Venue', path: '/admin/venues/new' },
                { label: 'Cooler', path: '/admin/cooler' },
                { label: 'Stage Designer', path: '/stage-designer' },
                { label: 'Band Management', path: '/admin/bands' }
              ],
              Tools: [
                { label: 'Pomodoro', path: '/musicadmin', icon: Wrench },
              ]
            }
          };
      case 'guest':
        return {
          mainItems: [
            { label: 'Dashboard', path: '/dashboard', icon: Home },
            { label: 'Cooler', path: '/admin/cooler' },
            { label: 'Add Expense', path: '/admin/expenses/new' },
            { label: 'Bands', path: '/bands', icon: Music },
            { label: 'Public Strands', path: '/publicstrands', icon: FileText },
          ],
        };
      default:
        return {
          mainItems: [
            { label: 'Published Strands', path: '/publicstrands', icon: FileText },
          ],
        };
    }
  };